import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Disc3, Heart, Tag, Repeat2, MapPin, Sparkles, Trophy, ExternalLink, Sun, Moon } from "lucide-react";
import { VerifiedBadge } from "@/components/ui/verified-badge";
import { Logo } from "@/components/Logo";
import type { TemplateProps, AlbumItem } from "../types";
import { AlbumDetailDrawer } from "../AlbumDetailDrawer";

export function ClassicTemplate({ bio: p, config, theme = "dark" }: TemplateProps) {
  const [selected, setSelected] = useState<AlbumItem | null>(null);
  const isDark = theme === "dark";
  const bg = isDark ? "bg-[#0a0a0a] text-white" : "bg-white text-neutral-900";
  const card = isDark ? "bg-white/5 border-white/10" : "bg-neutral-50 border-neutral-200";
  const muted = isDark ? "text-neutral-400" : "text-neutral-500";
  const chip = isDark ? "bg-white/10 text-white" : "bg-neutral-900/5 text-neutral-900";
  const otherTheme = isDark ? "light" : "dark";

  const displayName = config.title || p.display_name || p.username;
  const avatar = p.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${p.username}`;
  const bioText = config.subtitle ?? p.bio;
  const sections = config.sections;

  return (
    <div className={`min-h-screen ${bg} transition-colors`}>
      <div className="mx-auto max-w-xl px-5 py-10">
        <header className="mt-8 flex flex-col items-center text-center">
          <img loading="lazy" decoding="async"
            src={avatar}
            alt={displayName}
            className={`h-28 w-28 rounded-full object-cover ring-4 ${isDark ? "ring-white/10" : "ring-neutral-100"}`}
          />
          <h1 className="mt-5 flex items-center gap-2 font-display text-2xl font-bold">
            {displayName}
            {p.is_premium && <VerifiedBadge variant="shimmer" size={22} aria-label="Verificado" />}
          </h1>
          <div className={`mt-1 text-sm ${muted}`}>@{p.username}</div>
          {(p.city || p.country) && (
            <div className={`mt-1 inline-flex items-center gap-1 text-xs ${muted}`}>
              <MapPin className="h-3.5 w-3.5" />
              {[p.city, p.country].filter(Boolean).join(", ")}
            </div>
          )}
          {sections.about !== false && bioText && (
            <p className="mt-4 max-w-md text-sm leading-relaxed">{bioText}</p>
          )}

          {p.favorite_genres.length > 0 && (
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {p.favorite_genres.map((g) => (
                <span key={g} className={`rounded-full px-3 py-1 text-xs font-medium ${chip}`}>
                  {g}
                </span>
              ))}
            </div>
          )}
        </header>

        {sections.stats !== false && (
          <div className={`mt-8 grid grid-cols-4 gap-2 rounded-2xl border p-3 text-center ${card}`}>
            <Stat icon={<Sparkles className="h-3.5 w-3.5" />} label="Nível" value={p.level} muted={muted} />
            <Stat icon={<Disc3 className="h-3.5 w-3.5" />} label="Discos" value={p.counts.owned} muted={muted} />
            <Stat icon={<Trophy className="h-3.5 w-3.5" />} label="Conquistas" value={p.achievements_count} muted={muted} />
            <Stat label="Seguidores" value={p.counts.followers} muted={muted} />
          </div>
        )}

        {sections.collection !== false && (
          <Section title="Coleção" icon={<Disc3 className="h-4 w-4" />} items={p.owned} card={card} muted={muted} isDark={isDark} onSelect={setSelected} />
        )}
        {sections.wishlist !== false && (
          <Section title="Lista de interesse" icon={<Heart className="h-4 w-4" />} items={p.wishlist} card={card} muted={muted} isDark={isDark} onSelect={setSelected} />
        )}
        {sections.for_sale === true && (
          <Section title="À venda" icon={<Tag className="h-4 w-4" />} items={p.for_sale} showPrice card={card} muted={muted} isDark={isDark} onSelect={setSelected} />
        )}
        {sections.for_trade === true && (
          <Section title="Disponíveis para troca" icon={<Repeat2 className="h-4 w-4" />} items={p.for_trade} card={card} muted={muted} isDark={isDark} onSelect={setSelected} />
        )}

        {sections.links !== false && config.links.length > 0 && (
          <section className="mt-10 space-y-2">
            {config.links.map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noreferrer"
                className={`flex items-center justify-between rounded-2xl border px-4 py-3 text-sm font-semibold ${card}`}>
                <span>{l.label}</span>
                <ExternalLink className="h-4 w-4" />
              </a>
            ))}
          </section>
        )}

        <div className="mt-10 text-center">
          <Link
            to="/profile/$username"
            params={{ username: p.username }}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold ${isDark ? "bg-white text-black" : "bg-black text-white"}`}
          >
            Ver perfil completo <ExternalLink className="h-4 w-4" />
          </Link>
          <div className={`mt-8 flex items-center justify-center gap-3 text-[11px] ${muted}`}>
            <span>feito com</span>
            <Link to="/" aria-label="viny.club" className="inline-flex items-center">
              <Logo variant={isDark ? "light" : "dark"} size={16} withLink={false} className="opacity-90" />
            </Link>
            <Link
              to="/$username"
              params={{ username: p.username }}
              search={{ t: otherTheme as "light" | "dark" }}
              replace
              aria-label="Alternar tema"
              className={`ml-1 inline-flex h-7 w-7 items-center justify-center rounded-full border ${isDark ? "border-white/15 hover:bg-white/5" : "border-neutral-200 hover:bg-neutral-100"}`}
            >
              {isDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            </Link>
          </div>
        </div>
      </div>
      <AlbumDetailDrawer album={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} />
    </div>
  );
}

function Stat({ icon, label, value, muted }: { icon?: React.ReactNode; label: string; value: number; muted: string }) {
  return (
    <div>
      <div className="font-display text-lg font-bold">{value}</div>
      <div className={`flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider ${muted}`}>
        {icon}{label}
      </div>
    </div>
  );
}

function Section({
  title, icon, items, showPrice, card, muted, isDark, onSelect,
}: {
  title: string;
  icon: React.ReactNode;
  items: AlbumItem[];
  showPrice?: boolean;
  card: string;
  muted: string;
  isDark: boolean;
  onSelect: (a: AlbumItem) => void;
}) {
  if (!items.length) return null;
  return (
    <section className="mt-10">
      <h2 className="mb-4 flex items-center gap-2 font-display text-base font-bold">
        {icon} {title} <span className={`text-xs font-normal ${muted}`}>· {items.length}</span>
      </h2>
      <div className="grid grid-cols-3 gap-3">
        {items.slice(0, 30).map((a) => (
          <button
            type="button"
            key={a.id}
            onClick={() => onSelect(a)}
            className={`group overflow-hidden rounded-lg border text-left transition-transform hover:-translate-y-0.5 ${card}`}
          >
            <div className="relative aspect-square">
              {a.cover_url ? (
                <img src={a.cover_url} alt={a.title} loading="lazy" className="h-full w-full object-cover" />
              ) : (
                <div className={`grid h-full w-full place-items-center ${muted}`}><Disc3 className="h-6 w-6" /></div>
              )}
              {showPrice && a.price_brl != null && (
                <div className={`absolute right-1 top-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${isDark ? "bg-white text-black" : "bg-black text-white"}`}>
                  R$ {a.price_brl}
                </div>
              )}
            </div>
            <div className="p-2">
              <div className="truncate text-[11px] font-semibold">{a.title}</div>
              <div className={`truncate text-[10px] ${muted}`}>{a.artist}</div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
